import { useCallback, useEffect, useRef, useState } from "react";
import { Send, Users } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errText } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { ROLE_NAMES, formatDate, channelsFor, channelName } from "@/lib/constants";
import { hasProfanity, PROFANITY_WARNING } from "@/lib/profanity";

function Bubble({ m, mine }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.id}`}>
      <div className={`max-w-[85%] sm:max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#1f5f42] text-white rounded-br-md" : "bg-white border border-[#d1e7dd] rounded-bl-md"}`}>
        <div className={`text-xs font-bold flex items-center gap-1.5 ${mine ? "text-[#c8f2d6]" : "text-[#2e7d56]"}`}>
          {m.author}
          {m.role !== "user" && <span className="px-1.5 rounded bg-amber-400/20 text-amber-600 text-[10px] uppercase">{ROLE_NAMES[m.role]}</span>}
          {m.grade && <span className="opacity-70 font-medium">· {m.grade} кл.</span>}
        </div>
        <div className="text-sm mt-0.5 whitespace-pre-wrap break-words">{m.text}</div>
        <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-[#8aa699]"}`}>{formatDate(m.created_at)}</div>
      </div>
    </div>
  );
}

export default function ChatPage() {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [params, setParams] = useSearchParams();
  const channels = channelsFor(user);
  const channel = channels.some((c) => c.id === params.get("channel")) ? params.get("channel") : "general";
  const endRef = useRef(null);
  const lastId = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/chat", { params: { channel } }); setMsgs(data); } catch { /* polling */ }
  }, [channel]);
  useEffect(() => {
    setMsgs([]);
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [load]);
  useEffect(() => {
    const last = msgs[msgs.length - 1]?.id;
    if (last && last !== lastId.current) { lastId.current = last; endRef.current?.scrollIntoView({ behavior: "smooth" }); }
  }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (hasProfanity(text)) { toast.warning(PROFANITY_WARNING); return; }
    try { const { data } = await api.post("/chat", { text, channel }); setMsgs((p) => [...p, data]); setText(""); }
    catch (err) { toast.error(errText(err)); }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center gap-3 rise">
        <div className="h-12 w-12 rounded-xl bg-[#1f5f42] text-[#a3e6be] grid place-items-center"><Users className="h-6 w-6" /></div>
        <div>
          <h1 className="font-display text-2xl sm:text-4xl font-extrabold text-[#0b2e20]">Сообщество</h1>
          <p className="text-sm text-[#4b6b5c]" data-testid="chat-channel-title">{channelName(channel)}</p>
        </div>
      </div>
      <div className="mt-5 sm:mt-8 grid lg:grid-cols-[220px_1fr] gap-4">
      <div className="flex lg:flex-col gap-1.5 overflow-x-auto scrollbar-thin pb-1 lg:max-h-[60vh] lg:overflow-y-auto" data-testid="chat-channel-list">
        {channels.map((c, i) => (
          <div key={c.id} className="contents">
            {(i === 0 || channels[i - 1].group !== c.group) && <div className="hidden lg:block text-[11px] font-bold uppercase tracking-widest text-[#6b8a7b] mt-2 px-2">{c.group}</div>}
            <button onClick={() => setParams({ channel: c.id })} data-testid={`chat-channel-${c.id}`}
              className={`shrink-0 text-left px-3 py-2 rounded-full lg:rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${channel === c.id ? "bg-[#1f5f42] text-white" : "bg-white lg:bg-transparent border lg:border-0 border-[#d1e7dd] text-[#1f5f42] hover:bg-[#e8f5ed]"}`}>
              {c.name}
            </button>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-[#d1e7dd] bg-[#eef6f1] overflow-hidden">
        <div className="h-[calc(100dvh-390px)] min-h-[300px] lg:h-[55vh] overflow-y-auto p-3 sm:p-6 space-y-3 scrollbar-thin" data-testid="chat-messages-list">
          {msgs.length === 0 && <p className="text-center text-sm text-[#6b8a7b] py-20">Сообщений пока нет. Напиши первым!</p>}
          {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.author_id === user.id} />)}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="flex gap-2 p-3 bg-white border-t border-[#d1e7dd]">
          <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Написать сообщение..." className="h-11" data-testid="chat-input" />
          <Button type="submit" className="h-11 rounded-full bg-[#1f5f42] hover:bg-[#174731] px-5" data-testid="chat-send-button"><Send className="h-4 w-4" /></Button>
        </form>
      </div>
      </div>
    </div>
  );
}
